import {compactEth, formatPpm, formatTokens, formatAge, formatEth, type TokenSummary, type PeriodEntry} from '@colosseum/shared';
import {html, raw} from './dom.js';
import {explorerLink} from '../config.js';

/** Pieces shared by more than one view. */

const FALLBACK_ART = '/assets/token-0.webp';

/**
 * A token's image. The logo is whatever URL the creator wrote into the token
 * contract: only an https URL or a path on this site is used, anything else
 * shows the placeholder, and the page's URL is never sent to the image host.
 * The creator's host still sees the viewer's address when it serves the image,
 * as it would on any site that shows creator-hosted images.
 */
export function tokenArt(t: {logo?: string; symbol: string}, cls = ''): string {
  const src = t.logo && /^(https:\/\/|\/(?!\/))/.test(t.logo) ? t.logo : FALLBACK_ART;
  return html`<img class="${cls}" src="${src}" alt="" loading="lazy" referrerpolicy="no-referrer"
    data-fallback="${FALLBACK_ART}">`;
}

/** A token's card in the grid. Every number on it is the chain's. */
export function tokenCard(t: TokenSummary): string {
  const change = t.change24hPpm === null ? null : t.change24hPpm / 10_000;
  const dir = change === null ? 'muted' : change >= 0 ? 'up' : 'down';
  return html`<a class="token-card" href="#/token/${t.address}" aria-label="Open ${t.name}">
    <div class="art">
      ${raw(tokenArt(t))}
      ${raw(
        t.graduated
          ? '<span class="tag tag-pool" title="Trading in a locked Uniswap v4 pool">POOL</span>'
          : `<span class="tag tag-curve" title="Still on its bonding curve">${(t.progressPpm / 10_000).toFixed(0)}%</span>`,
      )}
      ${raw(t.qualified ? `<span class="tag tag-odds" title="Chance of winning this period's draw">${formatPpm(t.oddsPpm)}</span>` : '')}
    </div>
    <h3>${t.name}</h3>
    <p class="ticker">$${t.symbol}</p>
    <div class="card-row">
      <span class="${dir}">${change === null ? 'no 24h history' : `${change >= 0 ? '↑' : '↓'} ${Math.abs(change).toFixed(1)}%`}</span>
      <span class="holders" title="Holders, excluding the curve and the pool">♟ ${t.holders}</span>
    </div>
    <div class="bar" aria-hidden="true"><i style="width:${(t.progressPpm / 10_000).toFixed(1)}%"></i></div>
    <div class="card-stats">
      <div>${compactEth(BigInt(t.marketCapWei))}<small>MARKET, ETH</small></div>
      <div>${compactEth(BigInt(t.feesPeriodWei))}<small>FEES THIS PERIOD</small></div>
    </div>
  </a>`;
}

/**
 * The odds board: who is in this period's draw and what each one's chance is.
 * A token's chance is its share of the fees paid, so the bar is both at once.
 */
export function oddsBoard(entries: PeriodEntry[], belowThreshold: PeriodEntry[], thresholdVolume: string): string {
  if (entries.length === 0) {
    return html`<div class="odds-empty">
      <b>Nothing is in this draw yet.</b>
      <span>A token enters once its trades have paid 0.05 ETH of fees in this period,
      which is about ${thresholdVolume} ETH of volume. If nothing qualifies, the pool carries forward.</span>
      ${raw(
        belowThreshold.length
          ? `<div class="odds-waiting">Closest: ${belowThreshold
              .slice(0, 3)
              .map((e) => `${e.symbol} ${compactEth(BigInt(e.feesWei))} ETH`)
              .join(' · ')}</div>`
          : '',
      )}
    </div>`;
  }
  return html`<ol class="odds-board">
    ${entries.map(
      (e) => raw(html`<li>
        <a href="#/token/${e.token}">
          ${raw(tokenArt(e))}
          <span class="odds-name">${e.symbol}</span>
          <span class="odds-bar"><i style="width:${Math.max(1.5, e.oddsPpm / 10_000)}%"></i></span>
          <span class="odds-pct">${formatPpm(e.oddsPpm)}</span>
          <span class="odds-fees">${compactEth(BigInt(e.feesWei))} ETH</span>
        </a>
      </li>`),
    )}
    ${raw(
      belowThreshold.length
        ? html`<li class="odds-below">
            <span>${belowThreshold.length} more trading, none past the 0.05 ETH threshold yet</span>
          </li>`
        : '',
    )}
  </ol>`;
}

/** One row of the draw history. */
export function drawRow(d: {
  period: number; round: string; symbol: string | null; logo: string | null; winner: string | null;
  spentWei: string; burned: string; outcome: string; slices: number; fallback: boolean;
  time: number; txHash: string; latencySeconds: number | null;
}): string {
  const link = explorerLink('tx', d.txHash);
  const when = d.time ? formatAge(d.time) : '';
  if (d.outcome === 'skipped' || d.outcome === 'no-purchase') {
    return html`<li class="draw-row draw-skipped">
      <span class="draw-period">#${d.period}</span>
      <span class="draw-what">${d.outcome === 'skipped' ? 'No token qualified — the pool carried forward' : 'The pool was empty — carried forward'}</span>
      <span class="draw-when">${when}</span>
    </li>`;
  }
  const source = d.fallback
    ? html`<span class="draw-round" title="The beacon was unavailable for seven days; settled from a block hash committed ten blocks ahead">block-hash fallback</span>`
    : html`<span class="draw-round" title="drand quicknet round, verified on chain">round ${d.round}</span>`;
  if (d.outcome === 'pending') {
    return html`<li class="draw-row">
      <span class="draw-period">#${d.period}</span>
      ${raw(tokenArt({logo: d.logo ?? '', symbol: d.symbol ?? ''}, 'draw-art'))}
      <span class="draw-what">
        <b>${d.symbol ?? 'unknown'}</b> drawn, purchase in progress: ${formatEth(BigInt(d.spentWei), {places: 4})} ETH spent
        over ${d.slices} slice${d.slices === 1 ? '' : 's'} so far, <b>${formatTokens(BigInt(d.burned))}</b> burned
      </span>
      ${raw(source)}
      <span class="draw-when">${raw(link ? html`<a href="${link}" target="_blank" rel="noopener">${when}</a>` : when)}</span>
    </li>`;
  }
  return html`<li class="draw-row">
    <span class="draw-period">#${d.period}</span>
    ${raw(tokenArt({logo: d.logo ?? '', symbol: d.symbol ?? ''}, 'draw-art'))}
    <span class="draw-what">
      <b>${d.symbol ?? 'unknown'}</b> bought with ${formatEth(BigInt(d.spentWei), {places: 4})} ETH
      in ${d.slices} slice${d.slices === 1 ? '' : 's'}, <b>${formatTokens(BigInt(d.burned))}</b> burned
    </span>
    ${raw(source)}
    <span class="draw-when">${raw(link ? html`<a href="${link}" target="_blank" rel="noopener">${when}</a>` : when)}</span>
  </li>`;
}

export function addressChip(address: string, kind: 'address' | 'token' = 'address'): string {
  const short = `${address.slice(0, 6)}…${address.slice(-4)}`;
  const link = explorerLink(kind, address);
  return link
    ? html`<a class="chip mono" href="${link}" target="_blank" rel="noopener">${short}</a>`
    : html`<button class="chip mono" data-action="copy" data-copy="${address}" title="Copy ${address}">${short}</button>`;
}
